import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Collapse, Form, Input, Modal, Row, Select, Space, Statistic, Table, Tabs, Tag, Typography, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckOutlined, CloseOutlined, FileSearchOutlined, PlusOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import { api } from './api';

const { Text, Paragraph } = Typography;

type CountRow = {
  status?: string;
  recognition_status?: string | null;
  latest_status?: string | null;
  total: number;
};

type DocumentLearningOverview = {
  profiles: CountRow[];
  mappings: CountRow[];
  documents: CountRow[];
  quality: {
    total: number;
    warning_count: number;
    missing_field_count: number;
  } | null;
  tests: CountRow[];
};

type DocumentProfile = {
  id: string;
  name: string;
  companyName: string | null;
  documentOrigin: string | null;
  documentType: string;
  insuranceTypeName: string | null;
  layoutFamily: string | null;
  layoutVersion: string | null;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  matchSignatures: unknown[];
  expectedFields: unknown[];
  updatedAt: string;
};

type ProfileCoverageEvidence = {
  name: string;
  standardLabel: string | null;
  masterCoverageName: string | null;
  evidence: string | null;
  fileName: string;
};

type CoverageMapping = {
  id: string;
  sourceTerm: string;
  normalizedSourceTerm: string;
  standardLabel: string;
  standardCoverageCode: string | null;
  needsAnalysisItemId: string | null;
  insuranceTypeId: string | null;
  companyName: string | null;
  documentOrigin: string | null;
  documentType: string | null;
  documentProfileId: string | null;
  documentProfileName: string | null;
  status: 'SUGGESTED' | 'APPROVED' | 'VALIDATED' | 'REJECTED' | 'ARCHIVED';
  confidence: number | null;
  supportCount: number;
  evidenceExamples: unknown[];
  notes: string | null;
  approvedAt: string | null;
  updatedAt: string;
};

type UnknownDocument = {
  id: string;
  leadId: string;
  leadCompanyName: string | null;
  fileName: string;
  uploadedAt: string;
  analysisStatus: string;
  recognitionStatus: string;
  recognitionConfidence: number | null;
  documentProfileName: string | null;
  classificationCandidates: string[];
  warningCount: number;
  missingFieldCount: number;
};

type QualityReport = {
  id: string;
  policyFileName: string | null;
  leadCompanyName: string | null;
  documentProfileName: string | null;
  recognitionStatus: string;
  recognitionConfidence: number | null;
  textQualityScore: number | null;
  extractionQualityScore: number | null;
  missingExpectedFields: unknown[];
  lowConfidenceFields: unknown[];
  warnings: unknown[];
  createdAt: string;
};

type DocumentTestCase = {
  id: string;
  name: string;
  companyName: string | null;
  documentOrigin: string | null;
  documentType: string;
  insuranceTypeName: string | null;
  documentProfileName: string | null;
  fileName: string | null;
  latestResult: {
    message?: string;
    profileName?: string;
    mappingSuggestionsCreated?: number;
    signatures?: string[];
    coverageCandidates?: string[];
    inferred?: { companyName?: string | null; documentType?: string | null };
    insuranceKind?: { label?: string | null };
  } | null;
  latestStatus: 'PASS' | 'FAIL' | 'ERROR' | null;
  latestRunAt: string | null;
  isActive: boolean;
  updatedAt: string;
};

type DocumentLearningData = {
  overview: DocumentLearningOverview | null;
  profiles: DocumentProfile[];
  mappings: CoverageMapping[];
  unknownDocuments: UnknownDocument[];
  qualityReports: QualityReport[];
  testCases: DocumentTestCase[];
};

const emptyData: DocumentLearningData = {
  overview: null,
  profiles: [],
  mappings: [],
  unknownDocuments: [],
  qualityReports: [],
  testCases: []
};

type TestDocumentFormValues = {
  name: string;
  companyName?: string;
  documentOrigin?: string;
  documentType: 'AUTO' | 'POLICY' | 'OFFER' | 'TENDER' | 'CONDITION' | 'OTHER';
  fileName?: string;
  extractedText?: string;
  expectedPayloadText?: string;
  notes?: string;
};

type MappingGroup = {
  key: string;
  standardLabel: string;
  standardCoverageCode: string | null;
  status: CoverageMapping['status'];
  mappings: CoverageMapping[];
};

type MappingReviewFormValues = {
  standardLabel: string;
  masterCoverageCode?: string | null;
  scope: 'PROFILE' | 'COMPANY' | 'GLOBAL';
};

type CoverageMasterConcept = {
  coverageCode: string;
  coverageName: string;
  conceptType: string;
  category: string;
  applicationCode: string | null;
  usageReferences: string[];
  synonyms: string[];
};

const mappingStatusLabel: Record<CoverageMapping['status'], string> = {
  SUGGESTED: 'Forslag',
  APPROVED: 'Godkendt',
  VALIDATED: 'Valideret',
  REJECTED: 'Afvist',
  ARCHIVED: 'Arkiveret'
};

const mappingStatusColor: Record<CoverageMapping['status'], string> = {
  SUGGESTED: 'processing',
  APPROVED: 'success',
  VALIDATED: 'green',
  REJECTED: 'error',
  ARCHIVED: 'default'
};

const profileStatusColor: Record<DocumentProfile['status'], string> = {
  DRAFT: 'processing',
  ACTIVE: 'success',
  ARCHIVED: 'default'
};

const profileStatusLabel: Record<DocumentProfile['status'], string> = {
  DRAFT: 'Kladde',
  ACTIVE: 'Aktiv',
  ARCHIVED: 'Afvist'
};

const documentTypeLabel: Record<string, string> = {
  POLICY: 'Police',
  OFFER: 'Tilbud',
  TENDER: 'Udbud',
  CONDITION: 'Betingelse',
  OTHER: 'Andet',
  AUTO: 'Auto'
};

const testStatusLabel: Record<'PASS' | 'FAIL' | 'ERROR', string> = {
  PASS: 'Profilforslag oprettet',
  FAIL: 'Fejlet',
  ERROR: 'Kræver handling'
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('da-DK', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${Math.round(value * 100)} %`;
}

function totalFor(rows: CountRow[] | undefined, key: string): number {
  return Number(rows?.find((row) => row.status === key || row.recognition_status === key || row.latest_status === key)?.total || 0);
}

async function readJson<T>(response: Response, fallback: string): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || fallback);
  }
  return data as T;
}

export default function DocumentLearningPage() {
  const [data, setData] = useState<DocumentLearningData>(emptyData);
  const [coverageMaster, setCoverageMaster] = useState<CoverageMasterConcept[]>([]);
  const [coverageMasterSearch, setCoverageMasterSearch] = useState('');
  const [coverageMasterCategory, setCoverageMasterCategory] = useState<string | undefined>();
  const [coverageMasterConceptType, setCoverageMasterConceptType] = useState<string | undefined>();
  const [coverageMasterApplicationStatus, setCoverageMasterApplicationStatus] = useState<'MAPPED' | 'UNMAPPED' | undefined>();
  const [activeTab, setActiveTab] = useState('mappings');
  const [loading, setLoading] = useState(false);
  const [updatingMappingGroupKey, setUpdatingMappingGroupKey] = useState<string | null>(null);
  const [analyzingTestCaseId, setAnalyzingTestCaseId] = useState<string | null>(null);
  const [analyzingUnknownDocumentId, setAnalyzingUnknownDocumentId] = useState<string | null>(null);
  const [updatingProfileId, setUpdatingProfileId] = useState<string | null>(null);
  const [reviewProfile, setReviewProfile] = useState<DocumentProfile | null>(null);
  const [profileCoverageEvidence, setProfileCoverageEvidence] = useState<ProfileCoverageEvidence[]>([]);
  const [profileCoverageLoading, setProfileCoverageLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reviewMappingGroup, setReviewMappingGroup] = useState<MappingGroup | null>(null);
  const [testUploadOpen, setTestUploadOpen] = useState(false);
  const [savingTestCase, setSavingTestCase] = useState(false);
  const [extractingTestFile, setExtractingTestFile] = useState(false);
  const [testFileStatus, setTestFileStatus] = useState<string | null>(null);
  const [testUploadFile, setTestUploadFile] = useState<File | null>(null);
  const [testDocumentForm] = Form.useForm<TestDocumentFormValues>();
  const [mappingReviewForm] = Form.useForm<MappingReviewFormValues>();
  const coverageMasterByCode = new Map(coverageMaster.map((concept) => [concept.coverageCode, concept]));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, profilesRes, mappingsRes, unknownRes, qualityRes, testsRes, masterRes] = await Promise.all([
        api('/api/admin/document-learning/overview'),
        api('/api/admin/document-learning/profiles'),
        api('/api/admin/document-learning/mappings'),
        api('/api/admin/document-learning/unknown-documents'),
        api('/api/admin/document-learning/quality-reports'),
        api('/api/admin/document-learning/test-cases'),
        api('/api/admin/document-learning/coverage-master')
      ]);

      const [overview, profiles, mappings, unknownDocuments, qualityReports, testCases, master] = await Promise.all([
        readJson<DocumentLearningOverview>(overviewRes, 'Kunne ikke hente overblik'),
        readJson<{ profiles: DocumentProfile[] }>(profilesRes, 'Kunne ikke hente dokumentprofiler'),
        readJson<{ mappings: CoverageMapping[] }>(mappingsRes, 'Kunne ikke hente dækningsmappinger'),
        readJson<{ documents: UnknownDocument[] }>(unknownRes, 'Kunne ikke hente ukendte dokumenter'),
        readJson<{ reports: QualityReport[] }>(qualityRes, 'Kunne ikke hente kvalitetsrapporter'),
        readJson<{ testCases: DocumentTestCase[] }>(testsRes, 'Kunne ikke hente testbibliotek'),
        readJson<{ concepts: CoverageMasterConcept[] }>(masterRes, 'Kunne ikke hente dækningsmasteren')
      ]);

      setData({
        overview,
        profiles: Array.isArray(profiles.profiles) ? profiles.profiles : [],
        mappings: Array.isArray(mappings.mappings) ? mappings.mappings : [],
        unknownDocuments: Array.isArray(unknownDocuments.documents) ? unknownDocuments.documents : [],
        qualityReports: Array.isArray(qualityReports.reports) ? qualityReports.reports : [],
        testCases: Array.isArray(testCases.testCases) ? testCases.testCases : []
      });
      setCoverageMaster(Array.isArray(master.concepts) ? master.concepts : []);
      setLoadError(null);
    } catch (error: any) {
      const messageText = error?.message || 'Kunne ikke hente dokumentlæring';
      setLoadError(messageText);
      message.error(messageText);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateMappingGroupStatus = useCallback(async (group: MappingGroup, status: CoverageMapping['status']) => {
    setUpdatingMappingGroupKey(group.key);
    try {
      await Promise.all(group.mappings.map(async (mapping) => {
        const response = await api(`/api/admin/document-learning/mappings/${mapping.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...mapping, status })
        });
        await readJson(response, 'Kunne ikke opdatere mapping');
      }));
      message.success(status === 'APPROVED' ? 'Synonymgruppe godkendt' : 'Synonymgruppe opdateret');
      await load();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke opdatere synonymgruppe');
    } finally {
      setUpdatingMappingGroupKey(null);
    }
  }, [load]);

  const openMappingReview = useCallback((group: MappingGroup) => {
    const first = group.mappings[0];
    setReviewMappingGroup(group);
    const matchedConcept = coverageMaster.find((concept) =>
      concept.applicationCode === group.standardCoverageCode && concept.coverageName === group.standardLabel
    );
    mappingReviewForm.setFieldsValue({
      standardLabel: group.standardLabel,
      masterCoverageCode: matchedConcept?.coverageCode,
      scope: first?.documentProfileId ? 'PROFILE' : first?.companyName ? 'COMPANY' : 'GLOBAL'
    });
  }, [coverageMaster, mappingReviewForm]);

  const closeMappingReview = useCallback(() => {
    setReviewMappingGroup(null);
    mappingReviewForm.resetFields();
  }, [mappingReviewForm]);

  const saveMappingReview = useCallback(async () => {
    if (!reviewMappingGroup) return;
    try {
      const values = await mappingReviewForm.validateFields();
      const selectedConcept = values.masterCoverageCode ? coverageMasterByCode.get(values.masterCoverageCode) : undefined;
      const standardLabel = selectedConcept?.coverageName || values.standardLabel.trim();
      if (!standardLabel) {
        message.error('Vælg eller angiv et standardfelt');
        return;
      }

      setUpdatingMappingGroupKey(reviewMappingGroup.key);
      for (const mapping of reviewMappingGroup.mappings) {
        const response = await api(`/api/admin/document-learning/mappings/${mapping.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...mapping,
            standardLabel,
            standardCoverageCode: selectedConcept?.applicationCode || null,
            documentProfileId: values.scope === 'PROFILE' ? mapping.documentProfileId : null,
            companyName: values.scope === 'GLOBAL' ? null : mapping.companyName,
            status: 'APPROVED'
          })
        });
        await readJson(response, 'Kunne ikke gemme mapping');
      }
      message.success('Synonymgruppe gemt og godkendt');
      closeMappingReview();
      await load();
    } catch (error: any) {
      if (!error?.errorFields) message.error(error?.message || 'Kunne ikke gemme synonymgruppe');
    } finally {
      setUpdatingMappingGroupKey(null);
    }
  }, [closeMappingReview, coverageMasterByCode, load, mappingReviewForm, reviewMappingGroup]);

  const openTestUpload = useCallback(() => {
    testDocumentForm.setFieldsValue({
      name: '',
      companyName: '',
      documentOrigin: '',
      documentType: 'AUTO',
      fileName: '',
      extractedText: '',
      expectedPayloadText: '{}',
      notes: ''
    });
    setTestUploadFile(null);
    setTestFileStatus(null);
    setTestUploadOpen(true);
  }, [testDocumentForm]);

  const closeTestUpload = useCallback(() => {
    setTestUploadOpen(false);
    setTestUploadFile(null);
    setTestFileStatus(null);
    testDocumentForm.resetFields();
  }, [testDocumentForm]);

  const saveTestDocument = useCallback(async () => {
    try {
      const values = await testDocumentForm.validateFields();
      let expectedPayload: Record<string, unknown> = {};
      const expectedPayloadText = values.expectedPayloadText?.trim();
      if (expectedPayloadText) {
        const parsed = JSON.parse(expectedPayloadText);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Forventet facit skal være et JSON-objekt');
        }
        expectedPayload = parsed as Record<string, unknown>;
      }

      setSavingTestCase(true);
      let response: Response;
      if (testUploadFile && !values.extractedText?.trim()) {
        const formData = new FormData();
        formData.append('file', testUploadFile);
        formData.append('name', values.name.trim());
        if (values.documentType !== 'AUTO') formData.append('documentType', values.documentType);
        formData.append('expectedPayload', JSON.stringify(expectedPayload));
        if (values.companyName?.trim()) formData.append('companyName', values.companyName.trim());
        if (values.documentOrigin?.trim()) formData.append('documentOrigin', values.documentOrigin.trim());
        if (values.notes?.trim()) formData.append('notes', values.notes.trim());
        response = await api('/api/admin/document-learning/test-cases/upload', { method: 'POST', body: formData });
      } else {
        response = await api('/api/admin/document-learning/test-cases', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: values.name.trim(),
            companyName: values.companyName?.trim() || null,
            documentOrigin: values.documentOrigin?.trim() || null,
            documentType: values.documentType,
            fileName: values.fileName?.trim() || testUploadFile?.name || null,
            extractedText: values.extractedText?.trim() || null,
            expectedPayload,
            notes: values.notes?.trim() || null
          })
        });
      }
      const created = await readJson<{ testCase: DocumentTestCase }>(response, 'Kunne ikke oprette læringsdokument');
      if (created.testCase?.id) {
        const analysisResponse = await api(`/api/admin/document-learning/test-cases/${created.testCase.id}/run`, { method: 'POST' });
        await readJson(analysisResponse, 'Læringsdokumentet blev gemt, men kunne ikke analyseres');
      }
      message.success('Læringsdokument gemt og analyseret');
      closeTestUpload();
      await load();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.message || 'Kunne ikke gemme og analysere læringsdokument');
    } finally {
      setSavingTestCase(false);
    }
  }, [closeTestUpload, load, testDocumentForm, testUploadFile]);

  const readTestUploadFile = useCallback(async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      message.error('Filen må højst være 5 MB');
      return Upload.LIST_IGNORE;
    }
    const lowerName = file.name.toLowerCase();
    const isTextFile = lowerName.endsWith('.txt') || lowerName.endsWith('.md') || lowerName.endsWith('.json');
    const isOcrFile = /\.(pdf|png|jpg|jpeg|tif|tiff)$/i.test(lowerName);
    if (!isTextFile && !isOcrFile) {
      message.error('Upload en PDF/billedfil eller en .txt, .md eller .json med udtrukket tekst');
      return Upload.LIST_IGNORE;
    }

    setTestUploadFile(file);
    setTestFileStatus(null);
    testDocumentForm.setFieldsValue({
      name: testDocumentForm.getFieldValue('name') || file.name.replace(/\.[^.]+$/, ''),
      fileName: file.name
    });
    if (isTextFile) {
      const text = await file.text();
      testDocumentForm.setFieldsValue({ extractedText: text });
      setTestFileStatus('Tekstfilen er læst og klar til at blive gemt.');
    } else {
      setExtractingTestFile(true);
      testDocumentForm.setFieldsValue({ extractedText: '' });
      try {
        const formData = new FormData();
        formData.append('file', file);
        const companyName = testDocumentForm.getFieldValue('companyName');
        const documentType = testDocumentForm.getFieldValue('documentType');
        if (companyName?.trim()) formData.append('companyName', companyName.trim());
        if (documentType && documentType !== 'AUTO') formData.append('documentType', documentType);
        const response = await api('/api/admin/document-learning/test-cases/extract', { method: 'POST', body: formData });
        const data = await readJson<{
          fileName: string;
          extractedText: string;
          extractionMethod: string;
          inferred?: { companyName?: string | null; documentType?: TestDocumentFormValues['documentType'] };
        }>(response, 'Kunne ikke udtrække tekst fra dokumentet');
        const currentCompany = testDocumentForm.getFieldValue('companyName');
        const currentDocumentType = testDocumentForm.getFieldValue('documentType');
        testDocumentForm.setFieldsValue({
          fileName: data.fileName || file.name,
          extractedText: data.extractedText || '',
          companyName: currentCompany || data.inferred?.companyName || '',
          documentType: currentDocumentType && currentDocumentType !== 'AUTO' ? currentDocumentType : data.inferred?.documentType || 'AUTO'
        });
        setTestFileStatus(`Tekst udtrukket via ${data.extractionMethod}. Gennemgå evt. felterne og gem læringsdokumentet.`);
        message.success('Tekst udtrukket fra dokumentet');
      } catch (error: any) {
        setTestFileStatus(null);
        message.error(error?.message || 'Kunne ikke udtrække tekst fra dokumentet');
      } finally {
        setExtractingTestFile(false);
      }
    }
    return false;
  }, [testDocumentForm]);

  const analyzeTestCase = useCallback(async (testCase: DocumentTestCase) => {
    setAnalyzingTestCaseId(testCase.id);
    try {
      const response = await api(`/api/admin/document-learning/test-cases/${testCase.id}/run`, { method: 'POST' });
      const result = await readJson<{ testCase: DocumentTestCase }>(response, 'Kunne ikke analysere læringsdokument');
      message.success(result.testCase.latestResult?.profileName ? `Profilforslag oprettet: ${result.testCase.latestResult.profileName}` : 'Læringsdokument analyseret');
      await load();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke analysere læringsdokument');
    } finally {
      setAnalyzingTestCaseId(null);
    }
  }, [load]);

  const setProfileStatus = useCallback(async (profile: DocumentProfile, status: 'ACTIVE' | 'ARCHIVED') => {
    setUpdatingProfileId(profile.id);
    try {
      const response = await api(`/api/admin/document-learning/profiles/${profile.id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const result = await readJson<{ reprocessed?: number; recognized?: number }>(response, 'Kunne ikke opdatere dokumentprofil');
      message.success(
        status === 'ACTIVE'
          ? `Dokumentprofil aktiveret. ${result.recognized || 0} ukendte dokumenter blev genkendt.`
          : 'Dokumentprofil afvist'
      );
      await load();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke opdatere dokumentprofil');
    } finally {
      setUpdatingProfileId(null);
    }
  }, [load]);

  const openProfileReview = useCallback(async (profile: DocumentProfile) => {
    setReviewProfile(profile);
    setProfileCoverageEvidence([]);
    setProfileCoverageLoading(true);
    try {
      const response = await api(`/api/admin/document-learning/profiles/${profile.id}/coverage-evidence`);
      const result = await readJson<{ coverages: ProfileCoverageEvidence[] }>(response, 'Kunne ikke hente fundne dækninger');
      setProfileCoverageEvidence(Array.isArray(result.coverages) ? result.coverages : []);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente fundne dækninger');
    } finally {
      setProfileCoverageLoading(false);
    }
  }, []);

  const analyzeUnknownDocument = useCallback(async (document: UnknownDocument) => {
    setAnalyzingUnknownDocumentId(document.id);
    try {
      const response = await api(`/api/admin/document-learning/unknown-documents/${document.id}/analyze`, { method: 'POST' });
      const result = await readJson<{ analysis_status: 'COMPLETED' | 'FAILED'; analysis_error: string | null }>(response, 'Kunne ikke analysere dokumentet');
      if (result.analysis_status === 'FAILED') {
        throw new Error(result.analysis_error || 'AI-analysen fejlede');
      } else {
        message.success('AI-analysen er gennemført. Genkendelsesstatus og eventuelle forslag er opdateret.');
      }
      await load();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke analysere dokumentet');
    } finally {
      setAnalyzingUnknownDocumentId(null);
    }
  }, [load]);

  const chooseUnknownDocumentClassification = useCallback(async (document: UnknownDocument, insuranceKind: string) => {
    setAnalyzingUnknownDocumentId(document.id);
    try {
      const response = await api(`/api/admin/document-learning/unknown-documents/${document.id}/classification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ insuranceKind })
      });
      const result = await readJson<{ profileName: string; profileStatus: 'DRAFT' | 'ACTIVE' | 'ARCHIVED' }>(response, 'Kunne ikke gemme mæglerens valg');
      message.success(`Profilforslag oprettet: ${result.profileName}`);
      await load();
      setActiveTab(result.profileStatus === 'ACTIVE' ? 'activeProfiles' : 'profiles');
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke gemme mæglerens valg');
    } finally {
      setAnalyzingUnknownDocumentId(null);
    }
  }, [load]);

  const overview = data.overview;
  const approvedMappings = totalFor(overview?.mappings, 'APPROVED') + totalFor(overview?.mappings, 'VALIDATED');
  const unknownDocuments = totalFor(overview?.documents, 'UNKNOWN') + totalFor(overview?.documents, 'PARTIAL') + totalFor(overview?.documents, 'LOW_QUALITY');
  const failedTests = totalFor(overview?.tests, 'FAIL') + totalFor(overview?.tests, 'ERROR');
  const draftProfiles = data.profiles.filter((profile) => profile.status === 'DRAFT');
  const activeProfiles = data.profiles.filter((profile) => profile.status === 'ACTIVE');
  const standardCoverageOptions = coverageMaster
    .filter((concept) => concept.conceptType === 'coverage')
    .map((concept) => ({
      value: concept.coverageCode,
      label: `${concept.coverageName} - ${concept.usageReferences[0] || 'Faglig standarddækning'}`
    }))
    .sort((left, right) => left.label.localeCompare(right.label, 'da'));
  const coverageMasterCategories = Array.from(new Set(coverageMaster.map((concept) => concept.category))).sort((left, right) => left.localeCompare(right, 'da'));
  const coverageMasterConceptTypes = Array.from(new Set(coverageMaster.map((concept) => concept.conceptType))).sort((left, right) => left.localeCompare(right, 'da'));
  const filteredCoverageMaster = coverageMaster.filter((concept) => {
    const search = coverageMasterSearch.trim().toLocaleLowerCase('da');
    const matchesSearch = !search || [
      concept.coverageName,
      concept.coverageCode,
      ...concept.usageReferences,
      ...concept.synonyms
    ].some((value) => value.toLocaleLowerCase('da').includes(search));
    const matchesCategory = !coverageMasterCategory || concept.category === coverageMasterCategory;
    const matchesConceptType = !coverageMasterConceptType || concept.conceptType === coverageMasterConceptType;
    const matchesApplicationStatus = !coverageMasterApplicationStatus ||
      (coverageMasterApplicationStatus === 'MAPPED' ? Boolean(concept.applicationCode) : !concept.applicationCode);
    return matchesSearch && matchesCategory && matchesConceptType && matchesApplicationStatus;
  });
  const usageReferencesByApplicationCode = new Map<string, string[]>(
    coverageMaster
      .filter((concept) => Boolean(concept.applicationCode))
      .map((concept) => [concept.applicationCode as string, concept.usageReferences])
  );
  const mappingGroups = useMemo<MappingGroup[]>(() => {
    const groups = new Map<string, MappingGroup>();
    for (const mapping of data.mappings.filter((item) => item.status === 'SUGGESTED')) {
      const key = mapping.standardCoverageCode || mapping.standardLabel;
      const existing = groups.get(key);
      if (existing) {
        existing.mappings.push(mapping);
      } else {
        groups.set(key, {
          key,
          standardLabel: mapping.standardLabel,
          standardCoverageCode: mapping.standardCoverageCode,
          status: mapping.status,
          mappings: [mapping]
        });
      }
    }
    return Array.from(groups.values()).sort((left, right) => right.mappings.length - left.mappings.length || left.standardLabel.localeCompare(right.standardLabel, 'da'));
  }, [data.mappings]);
  const codedMappingGroups = mappingGroups.filter((group) => Boolean(group.standardCoverageCode));
  const uncodedMappingGroups = mappingGroups.filter((group) => !group.standardCoverageCode);
  const approvedMappingGroups = useMemo<MappingGroup[]>(() => {
    const groups = new Map<string, MappingGroup>();
    for (const mapping of data.mappings.filter((item) => item.status === 'APPROVED' || item.status === 'VALIDATED')) {
      const key = `${mapping.status}:${mapping.standardCoverageCode || mapping.standardLabel}`;
      const existing = groups.get(key);
      if (existing) {
        existing.mappings.push(mapping);
      } else {
        groups.set(key, {
          key,
          standardLabel: mapping.standardLabel,
          standardCoverageCode: mapping.standardCoverageCode,
          status: mapping.status,
          mappings: [mapping]
        });
      }
    }
    return Array.from(groups.values()).sort((left, right) => right.mappings.length - left.mappings.length || left.standardLabel.localeCompare(right.standardLabel, 'da'));
  }, [data.mappings]);

  const mappingColumns = useMemo<ColumnsType<MappingGroup>>(() => [
    {
      title: 'Standardfelt',
      key: 'standard',
      render: (_, group) => (
        <Space direction="vertical" size={0}>
          <Text strong>{group.standardLabel}</Text>
          <Text type="secondary">
            {group.standardCoverageCode
              ? usageReferencesByApplicationCode.get(group.standardCoverageCode)?.join(' · ') || 'Anvendes i Dokumentlæring'
              : 'Kræver valg af anvendelse'}
          </Text>
        </Space>
      )
    },
    {
      title: 'Synonymer fundet',
      key: 'synonyms',
      render: (_, group) => (
        <Space size={[4, 4]} wrap>
          {Array.from(new Set(group.mappings.map((mapping) => mapping.sourceTerm))).slice(0, 12).map((term) => <Tag key={term}>{term}</Tag>)}
        </Space>
      )
    },
    {
      title: 'Scope',
      key: 'scope',
      render: (_, group) => {
        const mappings = group.mappings;
        const profiles = Array.from(new Set(mappings.map((mapping) => mapping.documentProfileName).filter(Boolean))) as string[];
        const companies = Array.from(new Set(mappings.map((mapping) => mapping.companyName).filter(Boolean))) as string[];
        const types = Array.from(new Set(mappings.map((mapping) => mapping.documentType).filter(Boolean))) as string[];
        return (
        <Space size={[4, 4]} wrap>
          {profiles.slice(0, 2).map((profile) => <Tag key={profile}>{profile}</Tag>)}
          {companies.slice(0, 2).map((company) => <Tag key={company}>{company}</Tag>)}
          {types.map((type) => <Tag key={type}>{documentTypeLabel[type] || type}</Tag>)}
          {!profiles.length && !companies.length && !types.length ? <Text type="secondary">Globalt</Text> : null}
        </Space>
        );
      }
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, group) => <Tag color={mappingStatusColor[group.status]}>{mappingStatusLabel[group.status]}</Tag>
    },
    {
      title: 'Signal',
      key: 'signal',
      render: (_, group) => (
        <Space direction="vertical" size={0}>
          <Text>{group.mappings.reduce((sum, mapping) => sum + mapping.supportCount, 0)} observationer</Text>
          <Text type="secondary">{group.mappings.length} synonym{group.mappings.length === 1 ? '' : 'er'}</Text>
        </Space>
      )
    },
    {
      title: 'Handlinger',
      key: 'actions',
      width: 170,
      render: (_, group) => group.status === 'SUGGESTED' ? (
        <Space size={4}>
          <Button
            size="small"
            onClick={() => openMappingReview(group)}
          >
            Gennemgå
          </Button>
          <Button
            size="small"
            icon={<CheckOutlined />}
            disabled={!group.standardCoverageCode}
            loading={updatingMappingGroupKey === group.key}
            onClick={() => void updateMappingGroupStatus(group, 'APPROVED')}
          >
            Godkend gruppe
          </Button>
          <Button
            size="small"
            danger
            icon={<CloseOutlined />}
            loading={updatingMappingGroupKey === group.key}
            onClick={() => void updateMappingGroupStatus(group, 'REJECTED')}
          />
        </Space>
      ) : (
        <Button size="small" onClick={() => openMappingReview(group)}>Gennemgå</Button>
      )
    }
  ], [openMappingReview, updateMappingGroupStatus, updatingMappingGroupKey, usageReferencesByApplicationCode]);

  const profileColumns: ColumnsType<DocumentProfile> = [
    {
      title: 'Profil',
      key: 'profile',
      render: (_, profile) => (
        <Space direction="vertical" size={0}>
          <Text strong>{profile.name}</Text>
          <Text type="secondary">{[profile.layoutFamily, profile.layoutVersion].filter(Boolean).join(' · ') || 'Ingen layoutversion'}</Text>
        </Space>
      )
    },
    { title: 'Selskab', dataIndex: 'companyName', key: 'companyName', render: (value) => value || '—' },
    { title: 'Origin', dataIndex: 'documentOrigin', key: 'documentOrigin', render: (value) => value || '—' },
    { title: 'Type', dataIndex: 'documentType', key: 'documentType', render: (value) => <Tag>{documentTypeLabel[value] || value}</Tag> },
    { title: 'Forsikringstype', dataIndex: 'insuranceTypeName', key: 'insuranceTypeName', render: (value) => value || '—' },
    { title: 'Forventede felter', key: 'expectedFields', render: (_, profile) => profile.expectedFields.length },
    { title: 'Status', key: 'status', render: (_, profile) => <Tag color={profileStatusColor[profile.status]}>{profileStatusLabel[profile.status]}</Tag> },
    { title: 'Opdateret', dataIndex: 'updatedAt', key: 'updatedAt', render: formatDate },
    {
      title: 'Næste handling',
      key: 'actions',
      width: 210,
      render: (_, profile) => profile.status === 'DRAFT' ? (
        <Space size={4}>
          <Button
            size="small"
            onClick={() => void openProfileReview(profile)}
          >
            Gennemgå
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<CheckOutlined />}
            loading={updatingProfileId === profile.id}
            onClick={() => void openProfileReview(profile)}
          >
            Aktivér
          </Button>
          <Button
            size="small"
            danger
            icon={<CloseOutlined />}
            loading={updatingProfileId === profile.id}
            onClick={() => void setProfileStatus(profile, 'ARCHIVED')}
          >
            Afvis
          </Button>
        </Space>
      ) : <Text type="secondary">Ingen</Text>
    }
  ];

  const unknownColumns: ColumnsType<UnknownDocument> = [
    {
      title: 'Dokument',
      key: 'document',
      render: (_, document) => (
        <Space direction="vertical" size={0}>
          <Text strong>{document.fileName}</Text>
          <Text type="secondary">{document.leadCompanyName || document.leadId}</Text>
        </Space>
      )
    },
    { title: 'Analyse', dataIndex: 'analysisStatus', key: 'analysisStatus', render: (value) => <Tag>{value}</Tag> },
    { title: 'Genkendelse', key: 'recognition', render: (_, document) => <Space direction="vertical" size={0}><Tag color={document.recognitionStatus === 'LOW_QUALITY' ? 'error' : 'warning'}>{document.recognitionStatus}</Tag><Text type="secondary">{formatPercent(document.recognitionConfidence)}</Text></Space> },
    {
      title: 'Mæglervalg',
      key: 'classificationCandidates',
      render: (_, document) => document.classificationCandidates.length ? (
        <Space direction="vertical" size={2}>
          <Text type="warning">Vælg forsikringstype</Text>
          <Space size={[4, 4]} wrap>
            {document.classificationCandidates.map((candidate) => (
              <Button
                key={candidate}
                size="small"
                loading={analyzingUnknownDocumentId === document.id}
                onClick={() => void chooseUnknownDocumentClassification(document, candidate)}
              >
                {candidate}
              </Button>
            ))}
          </Space>
        </Space>
      ) : '—'
    },
    { title: 'Profil', dataIndex: 'documentProfileName', key: 'documentProfileName', render: (value) => value || '—' },
    { title: 'Mangler', dataIndex: 'missingFieldCount', key: 'missingFieldCount' },
    { title: 'Advarsler', dataIndex: 'warningCount', key: 'warningCount' },
    { title: 'Uploadet', dataIndex: 'uploadedAt', key: 'uploadedAt', render: formatDate },
    {
      title: 'Handlinger',
      key: 'actions',
      width: 160,
      render: (_, document) => (
        <Button
          size="small"
          icon={<FileSearchOutlined />}
          loading={analyzingUnknownDocumentId === document.id}
          onClick={() => void analyzeUnknownDocument(document)}
        >
          Analyser igen
        </Button>
      )
    }
  ];

  const qualityColumns: ColumnsType<QualityReport> = [
    {
      title: 'Rapport',
      key: 'report',
      render: (_, report) => (
        <Space direction="vertical" size={0}>
          <Text strong>{report.policyFileName || 'Ukendt dokument'}</Text>
          <Text type="secondary">{report.leadCompanyName || 'Ingen lead-reference'}</Text>
        </Space>
      )
    },
    { title: 'Profil', dataIndex: 'documentProfileName', key: 'documentProfileName', render: (value) => value || '—' },
    { title: 'Genkendelse', key: 'recognition', render: (_, report) => <Space direction="vertical" size={0}><Tag>{report.recognitionStatus}</Tag><Text type="secondary">{formatPercent(report.recognitionConfidence)}</Text></Space> },
    { title: 'Tekst', dataIndex: 'textQualityScore', key: 'textQualityScore', render: formatPercent },
    { title: 'Udtræk', dataIndex: 'extractionQualityScore', key: 'extractionQualityScore', render: formatPercent },
    { title: 'Mangler', key: 'missing', render: (_, report) => report.missingExpectedFields.length },
    { title: 'Lav confidence', key: 'lowConfidence', render: (_, report) => report.lowConfidenceFields.length },
    { title: 'Advarsler', key: 'warnings', render: (_, report) => report.warnings.length },
    { title: 'Oprettet', dataIndex: 'createdAt', key: 'createdAt', render: formatDate }
  ];

  const testColumns: ColumnsType<DocumentTestCase> = [
    {
      title: 'Testcase',
      key: 'test',
      render: (_, testCase) => (
        <Space direction="vertical" size={0}>
          <Text strong>{testCase.name}</Text>
          <Text type="secondary">{testCase.fileName || 'Ingen filnavn'}</Text>
        </Space>
      )
    },
    { title: 'Selskab', dataIndex: 'companyName', key: 'companyName', render: (value) => value || '—' },
    { title: 'Type', dataIndex: 'documentType', key: 'documentType', render: (value) => <Tag>{documentTypeLabel[value] || value}</Tag> },
    { title: 'Profil', key: 'profile', render: (_, testCase) => testCase.documentProfileName || testCase.latestResult?.profileName || '—' },
    {
      title: 'Seneste status',
      key: 'latestStatus',
      render: (_, testCase) => (
        <Space direction="vertical" size={2}>
          {testCase.latestStatus ? <Tag color={testCase.latestStatus === 'PASS' ? 'success' : 'error'}>{testStatusLabel[testCase.latestStatus]}</Tag> : <Tag>Ikke lært endnu</Tag>}
          {testCase.latestResult?.mappingSuggestionsCreated ? <Text type="secondary">{testCase.latestResult.mappingSuggestionsCreated} mappingforslag</Text> : null}
        </Space>
      )
    },
    { title: 'Senest kørt', dataIndex: 'latestRunAt', key: 'latestRunAt', render: formatDate },
    { title: 'Aktiv', dataIndex: 'isActive', key: 'isActive', render: (active) => <Tag color={active ? 'success' : 'default'}>{active ? 'Aktiv' : 'Inaktiv'}</Tag> },
    {
      title: 'Handlinger',
      key: 'actions',
      width: 140,
      render: (_, testCase) => (
        <Button
          size="small"
          icon={<FileSearchOutlined />}
          loading={analyzingTestCaseId === testCase.id}
          onClick={() => void analyzeTestCase(testCase)}
        >
          Lær igen
        </Button>
      )
    }
  ];

  const coverageMasterColumns: ColumnsType<CoverageMasterConcept> = [
    { title: 'Dækning', dataIndex: 'coverageName', key: 'coverageName' },
    { title: 'Begrebstype', dataIndex: 'conceptType', key: 'conceptType', render: (value) => <Tag color="blue">{value}</Tag> },
    { title: 'Kategori', dataIndex: 'category', key: 'category', render: (value) => <Tag>{value}</Tag> },
    { title: 'Masterkode', dataIndex: 'coverageCode', key: 'coverageCode' },
    {
      title: 'Anvendes i',
      key: 'usageReferences',
      render: (_, concept) => concept.usageReferences.length ? (
        <Space direction="vertical" size={0}>
          {concept.usageReferences.map((reference) => <Text key={reference}>{reference}</Text>)}
        </Space>
      ) : <Text type="secondary">Faglig reference</Text>
    },
    {
      title: 'Synonymer',
      key: 'synonyms',
      render: (_, concept) => (
        <Space size={[4, 4]} wrap>
          {concept.synonyms.slice(0, 12).map((synonym) => <Tag key={synonym}>{synonym}</Tag>)}
          {concept.synonyms.length > 12 ? <Text type="secondary">+{concept.synonyms.length - 12}</Text> : null}
        </Space>
      )
    }
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title="Dokumentlæring"
        extra={
          <Space size={8} wrap>
            <Button icon={<PlusOutlined />} type="primary" onClick={openTestUpload}>Upload læringsdokument</Button>
            <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>Opdater</Button>
          </Space>
        }
      >
        <Collapse
          size="small"
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'guide',
              label: 'Sådan bruges Dokumentlæring',
              children: (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Paragraph style={{ marginBottom: 0 }}>
                    Brug siden til at styre den fælles dokumentforståelse, som senere genbruges i AI-analyse, behovsanalyse og udbud.
                  </Paragraph>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>Upload et læringsdokument. Når du gemmer, udtrækker systemet tekst og opretter profil-/mappingforslag automatisk.</li>
                    <li>Gå til Profilforslag og klik “Aktivér”, når selskab, type og forsikringstype ser rigtige ud.</li>
                    <li>Gå til Mappingforslag og godkend kun forslag, når kildeord og standarddækning passer.</li>
                    <li>Brug kvalitetsrapporter til at finde manglende felter, lav confidence og dokumenter der kræver mæglergennemgang.</li>
                  </ul>
                </Space>
              )
            }
          ]}
        />
        {loadError ? <Alert type="warning" showIcon message="Dokumentlæring kunne ikke indlæses" description={loadError} style={{ marginBottom: 16 }} /> : null}
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}><Statistic title="Aktive profiler" value={totalFor(overview?.profiles, 'ACTIVE')} loading={loading} /></Col>
          <Col xs={12} md={6}><Statistic title="Mappinggrupper" value={codedMappingGroups.length} loading={loading} /></Col>
          <Col xs={12} md={6}><Statistic title="Godkendte mappings" value={approvedMappings} loading={loading} /></Col>
          <Col xs={12} md={6}><Statistic title="Ukendte/lav kvalitet" value={unknownDocuments} loading={loading} /></Col>
          <Col xs={12} md={6}><Statistic title="Kvalitetsrapporter" value={overview?.quality?.total || 0} loading={loading} /></Col>
          <Col xs={12} md={6}><Statistic title="Advarsler" value={overview?.quality?.warning_count || 0} loading={loading} /></Col>
          <Col xs={12} md={6}><Statistic title="Manglende felter" value={overview?.quality?.missing_field_count || 0} loading={loading} /></Col>
          <Col xs={12} md={6}><Statistic title="Fejlede tests" value={failedTests} loading={loading} /></Col>
        </Row>
      </Card>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'mappings',
              label: `Mappingforslag (${codedMappingGroups.length})`,
              children: (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Alert type="info" showIcon message="Godkend en synonymgruppe, når ordene til venstre betyder samme standardfelt. Godkendte grupper bruges i behovsanalyse og udbud." />
                  <Table rowKey="key" loading={loading} columns={mappingColumns} dataSource={codedMappingGroups} pagination={{ pageSize: 20, showSizeChanger: false }} />
                </Space>
              )
            },
            {
              key: 'uncertainMappings',
              label: `Usikre forslag (${uncodedMappingGroups.length})`,
              children: (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Alert type="warning" showIcon message="Disse forslag mangler standardfelt. Gennemgå dem og vælg korrekt standardfelt før godkendelse, eller afvis dem." />
                  <Table rowKey="key" loading={loading} columns={mappingColumns} dataSource={uncodedMappingGroups} pagination={{ pageSize: 20, showSizeChanger: false }} />
                </Space>
              )
            },
            {
              key: 'approvedMappings',
              label: `Godkendte mappinger (${approvedMappingGroups.length})`,
              children: (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Alert type="success" showIcon message="Disse synonymer er godkendt og anvendes automatisk i dokumentlæring, behovsanalyse og udbud." />
                  <Table rowKey="key" loading={loading} columns={mappingColumns} dataSource={approvedMappingGroups} pagination={{ pageSize: 20, showSizeChanger: false }} />
                </Space>
              )
            },
            {
              key: 'profiles',
              label: `Profilforslag (${draftProfiles.length})`,
              children: (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Alert type="info" showIcon message="Aktivér en profil, når forslaget ser rigtigt ud. Aktive profiler bruges automatisk på nye dokumenter." />
                  <Table rowKey="id" loading={loading} columns={profileColumns} dataSource={draftProfiles} pagination={{ pageSize: 20, showSizeChanger: false }} />
                </Space>
              )
            },
            {
              key: 'activeProfiles',
              label: `Aktive profiler (${activeProfiles.length})`,
              children: <Table rowKey="id" loading={loading} columns={profileColumns} dataSource={activeProfiles} pagination={{ pageSize: 20, showSizeChanger: false }} />
            },
            {
              key: 'coverageMaster',
              label: `Forsikringsontologi (${filteredCoverageMaster.length}/${coverageMaster.length})`,
              children: (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Alert type="info" showIcon message="Ontologien er den fælles kilde til forsikringsbegreber og synonymer. Automatisk dækningsmapping bruger kun begrebstypen coverage; øvrige begreber vises som faglig reference." />
                  <Space size={8} wrap>
                    <Input.Search
                      allowClear
                      placeholder="Søg begreb, masterkode, anvendelse eller synonym"
                      value={coverageMasterSearch}
                      onChange={(event) => setCoverageMasterSearch(event.target.value)}
                      style={{ width: 360, maxWidth: '100%' }}
                    />
                    <Select
                      allowClear
                      placeholder="Alle kategorier"
                      value={coverageMasterCategory}
                      onChange={setCoverageMasterCategory}
                      options={coverageMasterCategories.map((category) => ({ value: category, label: category }))}
                      style={{ width: 200, maxWidth: '100%' }}
                    />
                    <Select
                      allowClear
                      placeholder="Alle begrebstyper"
                      value={coverageMasterConceptType}
                      onChange={setCoverageMasterConceptType}
                      options={coverageMasterConceptTypes.map((conceptType) => ({ value: conceptType, label: conceptType }))}
                      style={{ width: 180, maxWidth: '100%' }}
                    />
                    <Select
                      allowClear
                      placeholder="Alle anvendelser"
                      value={coverageMasterApplicationStatus}
                      onChange={setCoverageMasterApplicationStatus}
                      options={[
                        { value: 'MAPPED', label: 'Anvendes i arbejdsgang' },
                        { value: 'UNMAPPED', label: 'Faglig reference' }
                      ]}
                      style={{ width: 190, maxWidth: '100%' }}
                    />
                  </Space>
                  <Table rowKey="coverageCode" loading={loading} columns={coverageMasterColumns} dataSource={filteredCoverageMaster} pagination={{ pageSize: 20, showSizeChanger: false }} />
                </Space>
              )
            },
            {
              key: 'unknown',
              label: `Kræver profil (${data.unknownDocuments.length})`,
              children: (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Alert type="info" showIcon message="Disse dokumenter er ikke færdigt genkendt endnu. Hvis der oprettes et profilforslag, skal profilen aktiveres før dokumentet forsvinder fra listen." />
                  <Table rowKey="id" loading={loading} columns={unknownColumns} dataSource={data.unknownDocuments} pagination={{ pageSize: 20, showSizeChanger: false }} />
                </Space>
              )
            },
            {
              key: 'quality',
              label: `Kvalitet (${data.qualityReports.length})`,
              children: <Table rowKey="id" loading={loading} columns={qualityColumns} dataSource={data.qualityReports} pagination={{ pageSize: 20, showSizeChanger: false }} />
            },
            {
              key: 'tests',
              label: `Læringsdokumenter (${data.testCases.length})`,
              children: (
                <Table
                  rowKey="id"
                  loading={loading}
                  columns={testColumns}
                  dataSource={data.testCases}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  expandable={{
                    expandedRowRender: (testCase) => testCase.latestResult ? (
                      <Space direction="vertical" size={6} style={{ width: '100%' }}>
                        <Text>{testCase.latestResult.message || 'Læringsanalyse gennemført.'}</Text>
                        <Text type="secondary">Forsikringstype: {testCase.latestResult.insuranceKind?.label || '—'}</Text>
                        <Text type="secondary">Foreslået profil: {testCase.latestResult.profileName || '—'}</Text>
                        {Array.isArray(testCase.latestResult.coverageCandidates) && testCase.latestResult.coverageCandidates.length ? (
                          <>
                            <Text strong>Fundne dækningskandidater</Text>
                            <Space size={[4, 4]} wrap>
                              {testCase.latestResult.coverageCandidates.slice(0, 16).map((candidate) => <Tag color="blue" key={candidate}>{candidate}</Tag>)}
                            </Space>
                          </>
                        ) : null}
                        {Array.isArray(testCase.latestResult.signatures) && testCase.latestResult.signatures.length ? (
                          <>
                            <Text strong>Layoutkendetegn</Text>
                          <Space size={[4, 4]} wrap>
                            {testCase.latestResult.signatures.slice(0, 8).map((signature) => <Tag key={signature}>{signature}</Tag>)}
                          </Space>
                          </>
                        ) : null}
                      </Space>
                    ) : <Text type="secondary">Ingen analyse endnu.</Text>
                  }}
                />
              )
            }
          ]}
        />
      </Card>

      <Modal
        title="Gennemgå dokumentprofil"
        open={Boolean(reviewProfile)}
        okText="Aktivér profil"
        cancelText="Luk"
        onCancel={() => { setReviewProfile(null); setProfileCoverageEvidence([]); }}
        onOk={() => reviewProfile && void setProfileStatus(reviewProfile, 'ACTIVE').then(() => setReviewProfile(null))}
        okButtonProps={{ disabled: reviewProfile?.status !== 'DRAFT' }}
        confirmLoading={Boolean(reviewProfile && updatingProfileId === reviewProfile.id)}
        destroyOnClose
        width={720}
      >
        {reviewProfile ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Alert type="warning" showIcon message="Kontrollér oplysningerne før aktivering. En aktiv profil bruges som genkendelsesregel på nye dokumenter." />
            <Row gutter={[16, 12]}>
              <Col span={12}><Text type="secondary">Selskab</Text><div><Text strong>{reviewProfile.companyName || 'Ikke identificeret'}</Text></div></Col>
              <Col span={12}><Text type="secondary">Forsikringstype</Text><div><Text strong>{reviewProfile.insuranceTypeName || 'Ikke identificeret'}</Text></div></Col>
              <Col span={12}><Text type="secondary">Dokumenttype</Text><div><Tag>{documentTypeLabel[reviewProfile.documentType] || reviewProfile.documentType}</Tag></div></Col>
              <Col span={12}><Text type="secondary">Origin/layout</Text><div><Text strong>{[reviewProfile.documentOrigin, reviewProfile.layoutFamily, reviewProfile.layoutVersion].filter(Boolean).join(' · ') || 'Ikke identificeret'}</Text></div></Col>
            </Row>
            <div>
              <Text strong>Kontrolfelter</Text>
              <Space size={[4, 4]} wrap style={{ display: 'flex', marginTop: 6 }}>
                {reviewProfile.expectedFields.length ? reviewProfile.expectedFields.map((field) => <Tag color="blue" key={String(field)}>{String(field)}</Tag>) : <Text type="secondary">Ingen defineret</Text>}
              </Space>
            </div>
            <div>
              <Text strong>Fundne dækninger i kildedokumentet</Text>
              {profileCoverageLoading ? <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>Henter fundne dækninger...</Text> : null}
              {!profileCoverageLoading && !profileCoverageEvidence.length ? <Text type="warning" style={{ display: 'block', marginTop: 6 }}>Ingen AI-udtrukne dækninger knyttet til denne profil endnu.</Text> : null}
              <Space direction="vertical" size={4} style={{ display: 'flex', marginTop: 6 }}>
                {profileCoverageEvidence.map((coverage, index) => (
                  <div key={`${coverage.fileName}-${coverage.name}-${index}`}>
                    <Text strong>{coverage.name}</Text>
                    {coverage.masterCoverageName || coverage.standardLabel ? <Text type="secondary"> · {coverage.masterCoverageName || coverage.standardLabel}</Text> : null}
                    <Text type="secondary"> · {coverage.fileName}</Text>
                    {coverage.evidence ? <div><Text type="secondary">{coverage.evidence}</Text></div> : null}
                  </div>
                ))}
              </Space>
            </div>
            <div>
              <Text strong>Dokumentkendetegn</Text>
              <Space size={[4, 4]} wrap style={{ display: 'flex', marginTop: 6 }}>
                {reviewProfile.matchSignatures.length ? reviewProfile.matchSignatures.map((signature) => <Tag key={String(signature)}>{String(signature)}</Tag>) : <Text type="secondary">Ingen registreret</Text>}
              </Space>
            </div>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title="Upload læringsdokument"
        open={testUploadOpen}
        okText="Gem og lær af dokument"
        cancelText="Annuller"
        onOk={() => void saveTestDocument()}
        onCancel={closeTestUpload}
        confirmLoading={savingTestCase}
        destroyOnClose
        maskClosable={false}
        width={760}
      >
        <Form form={testDocumentForm} layout="vertical" initialValues={{ documentType: 'AUTO', expectedPayloadText: '{}' }}>
          <Form.Item name="name" label="Navn" rules={[{ required: true, message: 'Angiv et navn' }]}>
            <Input placeholder="Protector bygning - Moderne 2025" maxLength={200} />
          </Form.Item>
          <Space size={12} style={{ width: '100%' }} align="start">
            <Form.Item name="companyName" label="Selskab" style={{ flex: 1 }}>
              <Input placeholder="Auto hvis tom" maxLength={120} />
            </Form.Item>
            <Form.Item name="documentOrigin" label="Origin" style={{ flex: 1 }}>
              <Input placeholder="Moderne" maxLength={120} />
            </Form.Item>
            <Form.Item name="documentType" label="Dokumenttype" rules={[{ required: true, message: 'Vælg dokumenttype' }]} style={{ minWidth: 160 }}>
              <Select
                options={[
                  { value: 'AUTO', label: 'Auto' },
                  { value: 'POLICY', label: 'Police' },
                  { value: 'OFFER', label: 'Tilbud' },
                  { value: 'TENDER', label: 'Udbud' },
                  { value: 'CONDITION', label: 'Betingelse' },
                  { value: 'OTHER', label: 'Andet' }
                ]}
              />
            </Form.Item>
          </Space>
          <Form.Item label="Dokumentfil">
            <Upload accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.txt,.md,.json" maxCount={1} beforeUpload={(file) => readTestUploadFile(file)} onRemove={() => { setTestUploadFile(null); return true; }}>
              <Button icon={<UploadOutlined />} loading={extractingTestFile}>Vælg PDF, billede eller tekstfil</Button>
            </Upload>
            <Text type="secondary">PDF og billeder sendes til backendens dokumentudtræk. Tekstfiler læses lokalt og gemmes direkte.</Text>
            {testUploadFile ? <div><Tag color="blue">Valgt fil</Tag><Text>{testUploadFile.name}</Text></div> : null}
            {extractingTestFile ? <Alert type="info" showIcon message="Udtrækker tekst fra dokumentet..." style={{ marginTop: 8 }} /> : null}
            {testFileStatus && !extractingTestFile ? <Alert type="success" showIcon message={testFileStatus} style={{ marginTop: 8 }} /> : null}
          </Form.Item>
          <Form.Item name="fileName" label="Filnavn">
            <Input placeholder="kunde-police-anonymiseret.txt" maxLength={240} />
          </Form.Item>
          <Form.Item
            name="extractedText"
            label="Udtrukket/anonymiseret tekst"
            rules={[
              {
                validator: (_, value) => {
                  if (testUploadFile || String(value || '').trim()) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Indsæt tekst eller upload en dokumentfil'));
                }
              }
            ]}
          >
            <Input.TextArea rows={8} placeholder="Indsæt anonymiseret policetekst her, eller upload en PDF/billedfil" />
          </Form.Item>
          <Form.Item name="expectedPayloadText" label="Forventet facit (JSON)">
            <Input.TextArea rows={5} placeholder={'{"coverages": [], "sums": [], "excel_fields": []}'} />
          </Form.Item>
          <Form.Item name="notes" label="Noter">
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Gennemgå synonymgruppe"
        open={Boolean(reviewMappingGroup)}
        okText="Gem og godkend"
        cancelText="Annuller"
        onOk={() => void saveMappingReview()}
        onCancel={closeMappingReview}
        confirmLoading={Boolean(reviewMappingGroup && updatingMappingGroupKey === reviewMappingGroup.key)}
        destroyOnClose
        width={720}
      >
        {reviewMappingGroup ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message="Vælg det standardfelt, som alle synonymerne skal pege på. Godkend kun gruppen, hvis ordene faktisk betyder det samme."
            />
            <Space size={[4, 4]} wrap>
              {Array.from(new Set(reviewMappingGroup.mappings.map((mapping) => mapping.sourceTerm))).map((term) => <Tag key={term}>{term}</Tag>)}
            </Space>
            <Form form={mappingReviewForm} layout="vertical">
              <Form.Item name="masterCoverageCode" label="Standarddækning">
                <Select
                  allowClear
                  showSearch
                  placeholder="Vælg standardfelt"
                  optionFilterProp="label"
                  options={standardCoverageOptions}
                  onChange={(value) => {
                    if (value) {
                      mappingReviewForm.setFieldsValue({ standardLabel: coverageMasterByCode.get(value)?.coverageName || '' });
                    }
                  }}
                />
              </Form.Item>
              <Form.Item name="standardLabel" label="Standardnavn" rules={[{ required: true, message: 'Angiv standardnavn' }]}>
                <Input placeholder="Fx Bygningsbrandforsikring" />
              </Form.Item>
              <Form.Item name="scope" label="Gælder for" rules={[{ required: true, message: 'Vælg scope' }]}>
                <Select
                  options={[
                    { value: 'PROFILE', label: 'Kun denne dokumentprofil' },
                    { value: 'COMPANY', label: 'Hele selskabet' },
                    { value: 'GLOBAL', label: 'Globalt' }
                  ]}
                />
              </Form.Item>
            </Form>
          </Space>
        ) : null}
      </Modal>
    </Space>
  );
}
